import { useCallback, useEffect, useMemo, useState } from 'react';

import { apiRequest } from '@/utils/api-request';

export type LlmModelOption = { label: string; value: string };

export interface LlmModelsResponse {
  openai: LlmModelOption[];
  anthropic: LlmModelOption[];
}

export const DEFAULT_LLM_MODEL = 'gpt-4.1-mini';

export const FALLBACK_LLM_MODELS: LlmModelsResponse = {
  openai: [
    { label: 'GPT-4.1 Mini', value: 'gpt-4.1-mini' },
    { label: 'GPT-4.1', value: 'gpt-4.1' },
    { label: 'GPT-5', value: 'gpt-5' },
  ],
  anthropic: [],
};

interface UseLLMModelsResult {
  options: LlmModelsResponse;
  isLoading: boolean;
  hasFetched: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useLLMModels(): UseLLMModelsResult {
  const [models, setModels] = useState<LlmModelsResponse>(FALLBACK_LLM_MODELS);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchModels = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const response = await apiRequest<LlmModelsResponse>({
      url: '/llm-models',
      method: 'GET',
      target: 'platform',
    });

    if (response.data) {
      setModels(response.data);
    } else if (response.detail) {
      setError(response.detail);
    } else {
      setError('Unable to load latest model list');
    }

    setIsLoading(false);
    setHasFetched(true);
  }, []);

  useEffect(() => {
    void fetchModels();
  }, [fetchModels]);

  const options = useMemo<LlmModelsResponse>(
    () => ({
      openai: models.openai ?? [],
      anthropic: models.anthropic ?? [],
    }),
    [models],
  );

  return {
    options,
    isLoading,
    hasFetched,
    error,
    refetch: fetchModels,
  };
}
